import { Variants } from '@pb/components'
import { consoleEvents } from '@pb/utils'
import { createReducer } from 'redux-act'

import getFirstMessageFromMessagesArray from 'helpers/getFirstMessageFromMessagesArray'

import actions from './actions'
import { initialState } from './initialState'
import {
  AddToBagVerificationData,
  ErrorProps,
  FavouritesState,
  NormalizedFavourites,
} from './types'

const fetchFavouritesStart = (state: FavouritesState): FavouritesState => ({
  ...state,
  isFavouritesLoading: true,
})

const fetchFavouritesSuccess = (
  state: FavouritesState,
  items: NormalizedFavourites[],
): FavouritesState => ({
  ...state,
  items,
  isFavouritesLoading: false,
  isFavouritesLoaded: true,
})

const fetchFavouritesFail = (state: FavouritesState, error: Error): FavouritesState => {
  consoleEvents.error('Failed to fetch favourites', error)
  return {
    ...state,
    isFavouritesLoading: false,
    isFavouritesLoadingFailed: true,
    isFavouritesLoaded: true,
  }
}

const fetchHiddenFavouritesStart = (state: FavouritesState): FavouritesState => ({
  ...state,
  isHiddenFavouritesLoading: true,
})

const fetchHiddenFavouritesSuccess = (
  state: FavouritesState,
  hiddenItems: FavouritesState['hiddenItems'],
): FavouritesState => ({
  ...state,
  hiddenItems,
  isHiddenFavouritesLoading: false,
  isHiddenFavouritesLoaded: true,
})

const fetchHiddenFavouritesFail = (state: FavouritesState, error: Error): FavouritesState => {
  consoleEvents.error('Failed to fetch hidden favourites', error)
  return {
    ...state,
    isHiddenFavouritesLoading: false,
    isHiddenFavouritesLoadingFailed: true,
  }
}

const showError = (
  state: FavouritesState,
  { displayMode, errorMessage, errorGroup, variantCode }: ErrorProps,
): FavouritesState => {
  return {
    ...state,
    favouritesError: {
      displayMode,
      ...(getFirstMessageFromMessagesArray(errorMessage) || {}),
      errorGroup,
      showError: true,
      variantCode,
    },
  }
}

const hideError = (state: FavouritesState): FavouritesState => {
  return {
    ...state,
    favouritesError: {
      errorGroup: '',
      showError: false,
      displayMode: {
        isModal: false,
        isNotificationBar: false,
      },
    },
  }
}

const deleteFavouritesItemStart = (
  state: FavouritesState,
  productCode: string,
): FavouritesState => ({
  ...state,
  items: state.items.filter((i: NormalizedFavourites) => i.code !== productCode),
  isFavouritesLoading: true,
})

const deleteFavouritesItemSuccess = (state: FavouritesState): FavouritesState => ({
  ...state,
  isFavouritesLoading: false,
})

const deleteFavouritesItemFail = (
  state: FavouritesState,
  {
    previousFavouritesItemsState,
    previousHiddenFavouritesItemsState,
  }: {
    previousFavouritesItemsState: FavouritesState['items']
    previousHiddenFavouritesItemsState: FavouritesState['hiddenItems']
  },
): FavouritesState => {
  consoleEvents.error('Failed to delete fav item')
  return {
    ...state,
    items: previousFavouritesItemsState,
    hiddenItems: previousHiddenFavouritesItemsState,
    isFavouritesLoading: false,
    isFavouritesLoadingFailed: true,
  }
}

const updateHiddenFavouritesItem = (
  state: FavouritesState,
  { updatedProductCode, productCode }: { updatedProductCode: string; productCode: string },
): FavouritesState => {
  const itemIndex = state.hiddenItems.findIndex(
    (item: NormalizedFavourites) => item.code === productCode,
  )
  const code = updatedProductCode

  return {
    ...state,
    hiddenItems: [
      ...state.hiddenItems.slice(0, itemIndex),
      { ...state.hiddenItems[itemIndex], code },
      ...state.hiddenItems.slice(itemIndex + 1),
    ],
  }
}

const deleteHiddenFavouritesItem = (
  state: FavouritesState,
  productCode: string,
): FavouritesState => {
  const itemIndex = state.hiddenItems.findIndex(
    (item: NormalizedFavourites) => item.code === productCode,
  )

  return {
    ...state,
    hiddenItems: [
      ...state.hiddenItems.slice(0, itemIndex),
      { ...state.hiddenItems[itemIndex], removed: true },
      ...state.hiddenItems.slice(itemIndex + 1),
    ],
  }
}

const moveToCartStart = (state: FavouritesState, variantCode: string): FavouritesState => {
  return {
    ...state,
    items: state.items.filter((i: NormalizedFavourites) => i.code !== variantCode),
    isFavouritesLoading: true,
  }
}

const moveToCartSuccess = (state: FavouritesState): FavouritesState => {
  return {
    ...state,
    isFavouritesLoading: false,
  }
}

const moveToCartFail = (
  state: FavouritesState,
  {
    previousFavouriteItemsState,
    previousHiddenFavouriteItemsState,
  }: {
    previousFavouriteItemsState: FavouritesState['items']
    previousHiddenFavouriteItemsState: FavouritesState['hiddenItems']
  },
): FavouritesState => {
  consoleEvents.error('Failed to move fav item to cart')

  return {
    ...state,
    items: previousFavouriteItemsState,
    hiddenItems: previousHiddenFavouriteItemsState,
    isFavouritesLoading: false,
    isFavouritesLoadingFailed: true,
  }
}

const showAddToBagVerificationStart = (state: FavouritesState): FavouritesState => ({
  ...state,
  addToBagVerificationItem: {},
  showAddToBagVerification: false,
  addToBagVerificationError: {
    showError: false,
    errorMessage: '',
  },
})

const showAddToBagVerificationSuccess = (
  state: FavouritesState,
  addToBagVerificationData: AddToBagVerificationData,
): FavouritesState => ({
  ...state,
  addToBagVerificationItem: addToBagVerificationData,
  showAddToBagVerification: true,
})

const showAddToBagVerificationError = (
  state: FavouritesState,
  {
    addToBagVerificationData,
    error,
  }: {
    addToBagVerificationData: AddToBagVerificationData
    error: Error
  },
): FavouritesState => {
  consoleEvents.error(error.message)
  return {
    ...state,
    showAddToBagVerification: true,
    addToBagVerificationError: {
      showError: true,
      errorMessage: addToBagVerificationData.errorLabel || '',
    },
  }
}

const hideAddToBagVerification = (state: FavouritesState): FavouritesState => ({
  ...state,
  addToBagVerificationItem: {},
  showAddToBagVerification: false,
  addToBagVerificationError: {
    showError: false,
    errorMessage: '',
  },
})

const updateVariantStart = (state: FavouritesState): FavouritesState => ({
  ...state,
  isFavouritesLoading: true,
})

const updateVariantSuccess = (
  state: FavouritesState,
  { newVariantCode, productCode }: { newVariantCode: string; productCode: string },
): FavouritesState => {
  const updatedItemIndex = state.items.findIndex(
    (item: NormalizedFavourites) => item.variantCode === productCode,
  )
  const newVariantIndex = state.items[updatedItemIndex]?.sizePickerData?.item.variants.findIndex(
    (variant: Variants) => variant.code === newVariantCode,
  ) as number
  const previousVariantIndex = state.items[
    updatedItemIndex
  ]?.sizePickerData?.item.variants.findIndex(
    (variant: Variants) => variant.code === productCode,
  ) as number
  return {
    ...state,
    items: state.items.map((item: NormalizedFavourites, itemIndex: number) =>
      itemIndex === updatedItemIndex
        ? {
            ...item,
            code: newVariantCode,
            variantCode: newVariantCode,
            showOutOfStockImageOverlay:
              !item.sizePickerData?.item.variants[newVariantIndex].stock.stockLevel,
            sizePickerData: {
              ...item.sizePickerData,
              item: {
                ...item.sizePickerData?.item,
                code: newVariantCode,
                variants: item.sizePickerData?.item.variants.map(
                  (variant: Variants, variantIndex: number) => {
                    if (variantIndex === newVariantIndex) return { ...variant, selected: true }
                    if (variantIndex === previousVariantIndex && productCode.length > 10) {
                      return { ...variant, selected: false }
                    }
                    return variant
                  },
                ),
              },
            },
          }
        : item,
    ) as NormalizedFavourites[],
    isFavouritesLoading: false,
  }
}

const updateVariantFail = (
  state: FavouritesState,
  {
    previousFavouritesItemsState,
    previousHiddenFavouritesItemsState,
  }: {
    previousFavouritesItemsState: FavouritesState['items']
    previousHiddenFavouritesItemsState: FavouritesState['hiddenItems']
  },
): FavouritesState => {
  consoleEvents.error('did NOT updated selected variant code')

  return {
    ...state,
    items: previousFavouritesItemsState,
    hiddenItems: previousHiddenFavouritesItemsState,
    isFavouritesLoading: false,
    isFavouritesLoadingFailed: true,
  }
}

const fetchStoreSearchResultsStart = (state: FavouritesState): FavouritesState => ({
  ...state,
  isStoreSearchResultsLoading: true,
})

const fetchStoreSearchResultsSuccess = (
  state: FavouritesState,
  storeSearchResults,
): FavouritesState => ({
  ...state,
  storeSearchResults,
  isStoreSearchResultsLoading: false,
})

const fetchStoreSearchResultsFail = (state: FavouritesState, error: Error): FavouritesState => {
  consoleEvents.error('Failed to fetch Stores', error)
  return {
    ...state,
    isStoreSearchResultsLoading: false,
    isStoreSearchResultsLoadingFailed: true,
  }
}

const resetStoreSearchResultsStart = (state: FavouritesState): FavouritesState => ({
  ...state,
  isStoreSearchResultsLoading: true,
})

const resetStoreSearchResultsSuccess = (state: FavouritesState): FavouritesState => ({
  ...state,
  storeSearchResults: [],
  isStoreSearchResultsLoading: false,
})

const resetStoreSearchResultsFail = (state: FavouritesState, error: Error): FavouritesState => {
  consoleEvents.error('Failed to reset Stores', error)
  return {
    ...state,
    isStoreSearchResultsLoading: false,
    isStoreSearchResultsLoadingFailed: true,
  }
}

const reducer = createReducer((on) => {
  on(actions.fetchFavouritesStart, fetchFavouritesStart)
  on(actions.fetchFavouritesSuccess, fetchFavouritesSuccess)
  on(actions.fetchFavouritesFail, fetchFavouritesFail)

  on(actions.fetchHiddenFavouritesStart, fetchHiddenFavouritesStart)
  on(actions.fetchHiddenFavouritesSuccess, fetchHiddenFavouritesSuccess)
  on(actions.fetchHiddenFavouritesFail, fetchHiddenFavouritesFail)

  on(actions.showError, showError)
  on(actions.hideError, hideError)

  on(actions.deleteFavouritesItemStart, deleteFavouritesItemStart)
  on(actions.deleteFavouritesItemSuccess, deleteFavouritesItemSuccess)
  on(actions.deleteFavouritesItemFail, deleteFavouritesItemFail)

  on(actions.updateHiddenFavouritesItem, updateHiddenFavouritesItem)
  on(actions.deleteHiddenFavouritesItem, deleteHiddenFavouritesItem)

  on(actions.moveToCartStart, moveToCartStart)
  on(actions.moveToCartSuccess, moveToCartSuccess)
  on(actions.moveToCartFail, moveToCartFail)

  on(actions.showAddToBagVerificationStart, showAddToBagVerificationStart)
  on(actions.showAddToBagVerificationSuccess, showAddToBagVerificationSuccess)
  on(actions.showAddToBagVerificationError, showAddToBagVerificationError)

  on(actions.hideAddToBagVerification, hideAddToBagVerification)

  on(actions.updateVariantStart, updateVariantStart)
  on(actions.updateVariantSuccess, updateVariantSuccess)
  on(actions.updateVariantFail, updateVariantFail)

  on(actions.fetchStoreSearchResultsStart, fetchStoreSearchResultsStart)
  on(actions.fetchStoreSearchResultsSuccess, fetchStoreSearchResultsSuccess)
  on(actions.fetchStoreSearchResultsFail, fetchStoreSearchResultsFail)

  on(actions.resetStoreSearchResultsStart, resetStoreSearchResultsStart)
  on(actions.resetStoreSearchResultsSuccess, resetStoreSearchResultsSuccess)
  on(actions.resetStoreSearchResultsFail, resetStoreSearchResultsFail)
}, initialState)

export default reducer
